import { Component, createRef } from 'react';
import { withTranslation } from 'react-i18next';
import html2canvas from 'html2canvas';
import moment from 'moment';

import { scrollToBlock } from '../../../helpers/scrollToBlock';

// The Search Console property list has one implementation in the app, on the tool that introduced
// it; every GSC tool picks a property out of everything Search Console exposes to the user, which
// is a wider list than the domains linked to GDDash.
import { getUserDomains } from '../../DomainPerformance/query';
import ScopeControls from '../GscTools/components/ScopeControls';
import CoverageStrip from '../GscTools/components/CoverageStrip';
import { ELIGIBLE_PERMISSIONS, probeLatestDate } from '../GscTools/query';
import { downloadCsv } from '../GscTools/csv';
import {
    buildSeries,
    comparability,
    coverageFor,
    hasDuplicateKeys,
    metricsFor,
    normalizeTotals,
    reportsPosition,
    sumRows,
} from '../GscTools/metrics';
import { defaultPeriod, detailPeriod, periodEndingOn } from '../GscTools/period';
import { readParams, writeParams } from '../GscTools/url';
// One export across the two overview reports: the same scope header, the same headline rows, the
// same daily columns. This report hands it a single source; it has no way to put two side by side.
import { buildReportCsv } from '../SearchOverview/exportRows';

import CountryDistribution from './components/CountryDistribution';
import ExtraControls from './components/ExtraControls';
import PageChanges from './components/PageChanges';
import PageTrend from './components/PageTrend';
import SourceCoverage from './components/SourceCoverage';
import SourceSummary from './components/SourceSummary';
import { fetchCountries, fetchPageChunk, fetchPageSeries, fetchPeriod } from './query';
import { countryRows, pageRows, pagesStopReason } from './rows';
import {
    URL_KEYS,
    hasReportScope,
    initialDraft,
    reportDraftFromParams,
    reportParamsFromScope,
    resolveReportScope,
} from './scope';
import { sourceLabel } from './sources';

const IDLE_PERIOD = { status: 'idle', totals: null, series: [], coverage: null, error: null };

const IDLE_PAGES = {
    status: 'idle',
    rows: [],
    currentChunk: null,
    priorChunk: null,
    // Whether the rows were joined against a baseline. False when no comparison was chosen and
    // when the baseline's pages failed to come back — the two cases look the same to the table.
    comparing: false,
    baselineError: null,
    filter: 'all',
    error: null,
    loadingMore: false,
    period: null,
    shortened: false,
};

const IDLE_COUNTRIES = { status: 'idle', rows: [], error: null };
const IDLE_PAGE_TREND = { status: 'idle', error: null, current: null, baseline: null };

const DEFAULT_METRIC = 'clicks';

/**
 * Discover & News — one Search Console property's performance on one of the three news surfaces,
 * read on its own.
 *
 * "News" is three products. Discover is the feed in the Google app and on the mobile homepage;
 * Google News is news.google.com and its apps; the News tab is a filter inside Google Search. They
 * are fetched one at a time, each as its own Search Console `type`, and nothing on this page ever
 * adds two of them together — a total across them would describe no surface anyone reads.
 *
 * Nor is any of them assumed to exist. A property can have Discover traffic and no Google News
 * traffic, or neither, and Search Console answering a source with nothing is an ordinary result.
 * The page says "no data returned for this source", which is a statement about the property, and
 * keeps that apart from a request that failed, which is not.
 *
 * The selected source decides which metrics there are. Discover and Google News report no ranking
 * position and no query dimension, so there is no position card and no query drilldown for them —
 * left out entirely rather than shown empty.
 *
 * Requests, per applied scope: each window's totals and daily series, each window's page rows, and
 * the current window's countries — each landing on its own, so one block failing leaves the others
 * standing. Opening a page adds that page's own daily series. Nothing is persisted; the scope lives
 * in the page's address, so a reload or a shared link runs the same report again.
 */
class DiscoverNews extends Component {
    constructor(props) {
        super(props);

        this.handleDraftChange = this.handleDraftChange.bind(this);
        this.handleApply = this.handleApply.bind(this);
        this.handleRefresh = this.handleRefresh.bind(this);
        this.handleToggleControls = this.handleToggleControls.bind(this);
        this.handleSelectMetric = this.handleSelectMetric.bind(this);
        this.handleFilterPages = this.handleFilterPages.bind(this);
        this.handleLoadMorePages = this.handleLoadMorePages.bind(this);
        this.handleSelectPage = this.handleSelectPage.bind(this);
        this.handleClearPage = this.handleClearPage.bind(this);
        this.handleSelectCountry = this.handleSelectCountry.bind(this);
        this.handleClearCountry = this.handleClearCountry.bind(this);
        this.handleExport = this.handleExport.bind(this);
        this.handlePrint = this.handlePrint.bind(this);
        this.handleDownloadImage = this.handleDownloadImage.bind(this);

        this.credentialsDomainId = props.credentials_domain_id || '';
        this.reportRef = createRef();
        this.pageTrendRef = createRef();

        // Bumped by every apply and refresh; a response carrying an older value is stale.
        this.generation = 0;
        this.pageTrendGeneration = 0;
        this.isMounted_ = false;

        const period = defaultPeriod();

        // The address is read once, here, so the controls show the report it names from the first
        // paint rather than flicking from the defaults to it a moment later.
        const params = readParams();
        this.urlDraft = reportDraftFromParams(params);
        this.urlHasScope = hasReportScope(params);

        this.state = {
            properties: [],
            loadingProperties: true,
            propertiesError: null,
            latestDate: '',

            draft: { ...initialDraft(period), ...this.urlDraft },

            applied: null,
            loading: false,
            fetchedAt: null,
            controlsOpen: true,
            capturing: false,

            // One metric for the whole page: the summary cards, the source trend and a page's own
            // trend all draw the same thing, so picking a card anywhere re-points all of them.
            metric: DEFAULT_METRIC,

            current: { ...IDLE_PERIOD },
            baseline: { ...IDLE_PERIOD },
            pages: { ...IDLE_PAGES },
            countries: { ...IDLE_COUNTRIES },

            selectedPage: null,
            pageTrend: { ...IDLE_PAGE_TREND },
        };
    }

    async componentDidMount() {
        this.isMounted_ = true;

        let properties = [];
        let propertiesError = null;

        try {
            const sites = await getUserDomains();
            properties = sites.filter((site) => ELIGIBLE_PERMISSIONS.has(site.permissionLevel));
        } catch (err) {
            console.log(err);
            propertiesError = err?.message ?? this.props.t('global.something_went_wrong');
        }

        if (!this.isMounted_) return;

        // A link naming a property this account cannot read is not quietly answered with another
        // property's figures: the first one is selected and the scope bar is left open for the
        // user to confirm, rather than a report being run under a scope nobody asked for.
        const requested = this.urlDraft.siteUrl
            ? properties.find((item) => item.siteUrl === this.urlDraft.siteUrl)
            : null;
        const property = requested ?? properties[0];
        const autoApply = this.urlHasScope && !!property && (!this.urlDraft.siteUrl || !!requested);

        this.setState(
            (state) => ({
                properties,
                propertiesError,
                loadingProperties: false,
                draft: property
                    ? {
                          ...state.draft,
                          siteUrl: property.siteUrl,
                          permissionLevel: property.permissionLevel,
                      }
                    : state.draft,
            }),
            () => {
                // Dates that came from the address are the user's own: the probe still runs, for
                // the preset anchor, but it does not move them.
                if (property) {
                    this.suggestDates(property.siteUrl, { seedDates: !this.urlDraft.startDate });
                }
                if (autoApply) this.handleApply();
            },
        );
    }

    componentWillUnmount() {
        this.isMounted_ = false;
    }

    /**
     * Seeds the date controls from the property's most recent activity on the selected source, so
     * the window the user is offered has something in it. Discover's latest day is not Web
     * Search's latest day, which is why the source travels with the probe.
     */
    async suggestDates(siteUrl, { seedDates = true } = {}) {
        if (!this.credentialsDomainId || !siteUrl) return;

        try {
            const latest = await probeLatestDate({
                credentialsDomainId: this.credentialsDomainId,
                siteUrl,
                searchType: this.state.draft.searchType,
            });

            if (!latest || !this.isMounted_) return;

            this.setState((state) => {
                if (state.draft.siteUrl !== siteUrl) return null;
                if (!seedDates) return { latestDate: latest };

                return { latestDate: latest, draft: { ...state.draft, ...periodEndingOn(latest) } };
            });
        } catch (err) {
            // A probe that fails costs nothing: the controls keep their default window.
            console.log(err);
        }
    }

    scope() {
        return resolveReportScope(this.state.draft, this.props.t);
    }

    /** The metric the page draws, held to the ones the applied source actually reports. */
    metric() {
        const source = this.state.applied?.searchType ?? this.state.draft.searchType;
        return metricsFor(source).includes(this.state.metric) ? this.state.metric : DEFAULT_METRIC;
    }

    handleDraftChange(patch) {
        this.setState(
            (state) => ({ draft: { ...state.draft, ...patch } }),
            () => {
                if (patch.siteUrl) {
                    const property = this.state.properties.find(
                        (item) => item.siteUrl === patch.siteUrl,
                    );
                    this.setState(
                        (state) => ({
                            draft: {
                                ...state.draft,
                                permissionLevel: property?.permissionLevel ?? '',
                            },
                        }),
                        () => this.suggestDates(patch.siteUrl),
                    );
                    return;
                }

                // A different source has a different most recent day; the preset anchor follows it.
                if (patch.searchType)
                    this.suggestDates(this.state.draft.siteUrl, { seedDates: false });
            },
        );
    }

    handleToggleControls() {
        this.setState((state) => ({ controlsOpen: !state.controlsOpen }));
    }

    handleApply() {
        const scope = this.scope();
        if (scope.errors.length) return;
        this.load(scope);
    }

    handleRefresh() {
        if (this.state.applied) this.load(this.state.applied);
    }

    /**
     * Runs the applied scope, every block's request at once and each landing on its own.
     *
     * Everything the previous scope produced is cleared first — the summary, the pages, the
     * countries and any open page. They belong to the source and window that fetched them, and a
     * page's Discover history left on screen after switching to Google News would be one source's
     * figures under another's heading.
     */
    load(scope) {
        this.generation += 1;
        this.pageTrendGeneration += 1;
        const generation = this.generation;

        // Written here rather than in handleApply so the address also follows a refresh and a
        // country picked from block 04, and only ever describes a scope that was actually run.
        writeParams(reportParamsFromScope(scope), URL_KEYS);

        this.setState((state) => ({
            applied: scope,
            controlsOpen: false,
            loading: true,
            fetchedAt: new Date().toISOString(),
            metric: metricsFor(scope.searchType).includes(state.metric)
                ? state.metric
                : DEFAULT_METRIC,

            current: { ...IDLE_PERIOD, status: 'loading' },
            baseline: scope.baseline ? { ...IDLE_PERIOD, status: 'loading' } : { ...IDLE_PERIOD },
            pages: { ...IDLE_PAGES, status: 'loading' },
            countries: { ...IDLE_COUNTRIES, status: 'loading' },

            selectedPage: null,
            pageTrend: { ...IDLE_PAGE_TREND },
        }));

        const periods = [this.loadPeriod(scope, scope.current, 'current', generation)];
        if (scope.baseline) {
            periods.push(this.loadPeriod(scope, scope.baseline, 'baseline', generation));
        }

        Promise.all(periods).then(() => {
            if (this.isMounted_ && generation === this.generation)
                this.setState({ loading: false });
        });

        this.loadPages(scope, generation);
        this.loadCountries(scope, generation);
    }

    /** Blocks 01 and 02 — one window's totals and daily series. */
    async loadPeriod(scope, period, key, generation) {
        try {
            const { totals, daily } = await fetchPeriod({
                credentialsDomainId: this.credentialsDomainId,
                scope,
                period,
            });

            if (!this.isMounted_ || generation !== this.generation) return;

            const hasPosition = reportsPosition(scope.searchType);

            this.setState({
                [key]: {
                    status: 'done',
                    // An empty successful response comes back as null here, not as zeroes: the
                    // source returned nothing for this property, which is not the same thing.
                    totals: normalizeTotals(totals, hasPosition),
                    series: buildSeries(daily, period, hasPosition),
                    coverage: coverageFor(daily, period),
                    error: null,
                },
            });
        } catch (err) {
            console.log(key, err);
            if (!this.isMounted_ || generation !== this.generation) return;

            this.setState({
                [key]: {
                    ...IDLE_PERIOD,
                    status: 'error',
                    error: err?.message ?? this.props.t('global.something_went_wrong'),
                },
            });
        }
    }

    /**
     * Block 03 — one chunk of page rows per window, joined on the exact URL.
     *
     * The two windows are fetched separately and settle separately. A baseline that fails takes
     * the changes away but not the pages: the current window's rows are still shown, on their own,
     * with the reason the changes are missing above them.
     *
     * Each window keeps its own cursor. Search Console can return a full chunk for one window and
     * a short one for the other, and advancing them together would either re-read rows or skip
     * them. A URL repeated inside one window stops that window instead of being counted twice.
     */
    async loadPages(scope, generation, { more = false } = {}) {
        const { period, shortened } = detailPeriod(scope.current);
        const baselineDetail = scope.baseline ? detailPeriod(scope.baseline) : null;

        this.setState((state) => ({
            pages: {
                ...state.pages,
                status: more ? state.pages.status : 'loading',
                loadingMore: more,
                period,
                shortened: shortened || !!baselineDetail?.shortened,
                error: null,
            },
        }));

        const previous = this.state.pages;

        const request = (detail, chunk) => {
            if (!detail) return Promise.resolve(null);
            if (more && (!chunk || chunk.nextOffset === null)) return Promise.resolve(chunk);

            return fetchPageChunk({
                credentialsDomainId: this.credentialsDomainId,
                scope,
                period: detail,
                offset: more ? chunk.nextOffset : 0,
            }).then((result) => {
                const rows = [...(chunk?.rows ?? []), ...result.rows];
                if (hasDuplicateKeys(rows))
                    throw new Error(this.props.t('tools.gsc.duplicate_rows'));
                return { ...result, rows };
            });
        };

        const [currentResult, priorResult] = await Promise.allSettled([
            request(period, more ? previous.currentChunk : null),
            // A baseline that already failed is not asked again on "load more"; it stays failed
            // until the next apply.
            more && previous.baselineError
                ? Promise.resolve(null)
                : request(baselineDetail?.period, more ? previous.priorChunk : null),
        ]);

        if (!this.isMounted_ || generation !== this.generation) return;

        if (currentResult.status === 'rejected') {
            console.log('pages', currentResult.reason);
            const message =
                currentResult.reason?.message ?? this.props.t('global.something_went_wrong');

            this.setState((state) => ({
                pages: {
                    ...state.pages,
                    // A failure while loading more keeps the rows already joined; a failure on the
                    // first chunk has nothing to keep.
                    status: state.pages.rows.length ? 'done' : 'error',
                    loadingMore: false,
                    error: message,
                },
            }));
            return;
        }

        const currentChunk = currentResult.value;
        const baselineFailed = priorResult.status === 'rejected';
        const priorChunk = baselineFailed ? null : priorResult.value;

        if (baselineFailed) console.log('baseline pages', priorResult.reason);

        const baselineError = baselineFailed
            ? priorResult.reason?.message ?? this.props.t('global.something_went_wrong')
            : more
            ? previous.baselineError
            : null;
        const comparing = !!scope.baseline && !baselineError;

        this.setState((state) => ({
            pages: {
                ...state.pages,
                status: 'done',
                loadingMore: false,
                currentChunk,
                priorChunk,
                comparing,
                baselineError,
                // A filter of gains or declines means nothing without a baseline to move against.
                filter: comparing ? state.pages.filter : 'all',
                rows: pageRows({
                    currentRows: currentChunk?.rows,
                    priorRows: priorChunk?.rows,
                    comparing,
                    hasPosition: reportsPosition(scope.searchType),
                }),
            },
        }));
    }

    handleLoadMorePages() {
        const scope = this.state.applied;
        if (scope) this.loadPages(scope, this.generation, { more: true });
    }

    handleFilterPages(filter) {
        this.setState((state) => ({ pages: { ...state.pages, filter } }));
    }

    /** Block 04 — the current window's countries. */
    async loadCountries(scope, generation) {
        try {
            const rows = await fetchCountries({
                credentialsDomainId: this.credentialsDomainId,
                scope,
            });

            if (!this.isMounted_ || generation !== this.generation) return;

            this.setState({
                countries: {
                    status: 'done',
                    rows: countryRows(rows, reportsPosition(scope.searchType)),
                    error: null,
                },
            });
        } catch (err) {
            console.log('countries', err);
            if (!this.isMounted_ || generation !== this.generation) return;

            this.setState({
                countries: {
                    ...IDLE_COUNTRIES,
                    status: 'error',
                    error: err?.message ?? this.props.t('global.something_went_wrong'),
                },
            });
        }
    }

    /**
     * Picking a country scopes the whole report to it; picking it again takes the filter off.
     *
     * Only the country changes. The new scope is the applied one with its country replaced, not the
     * draft in the scope bar — so an unapplied edit sitting in the bar is not applied behind the
     * user's back by a click in a table. The draft's country follows, so the bar and the report
     * agree on what is filtered.
     */
    handleSelectCountry(code) {
        const applied = this.state.applied;
        if (!applied) return;

        const country = code === applied.country ? '' : code;

        this.setState((state) => ({ draft: { ...state.draft, country } }));
        this.load({ ...applied, country });
    }

    handleClearCountry() {
        const applied = this.state.applied;
        if (!applied?.country) return;

        this.setState((state) => ({ draft: { ...state.draft, country: '' } }));
        this.load({ ...applied, country: '' });
    }

    handleSelectMetric(metric) {
        this.setState({ metric });
    }

    /**
     * Opening a page fetches its own daily series in each window and brings the block it fills into
     * view — every time, because that block sits below the fold and a pick that left the page where
     * it was would look like nothing happened. scrollToBlock leaves the page alone when the block is
     * already readable.
     */
    handleSelectPage(page) {
        const scope = this.state.applied;
        this.pageTrendGeneration += 1;
        const pageTrendGeneration = this.pageTrendGeneration;

        if (!page || !scope) {
            return this.setState({ selectedPage: null, pageTrend: { ...IDLE_PAGE_TREND } });
        }

        this.setState(
            { selectedPage: page, pageTrend: { ...IDLE_PAGE_TREND, status: 'loading' } },
            () => scrollToBlock(this.pageTrendRef.current),
        );

        this.loadPageTrend(scope, page, pageTrendGeneration);
    }

    handleClearPage() {
        this.pageTrendGeneration += 1;
        this.setState({ selectedPage: null, pageTrend: { ...IDLE_PAGE_TREND } });
    }

    async loadPageTrend(scope, page, pageTrendGeneration) {
        const hasPosition = reportsPosition(scope.searchType);

        const read = (rows, period) =>
            period
                ? {
                      series: buildSeries(rows, period, hasPosition),
                      coverage: coverageFor(rows, period),
                      // Summed from the days returned, so the cards and the lines are the same
                      // figures; CTR is summed clicks over summed impressions.
                      totals: sumRows(rows, hasPosition),
                  }
                : null;

        try {
            const { current, baseline } = await fetchPageSeries({
                credentialsDomainId: this.credentialsDomainId,
                scope,
                page,
            });

            if (!this.isMounted_ || pageTrendGeneration !== this.pageTrendGeneration) return;

            this.setState({
                pageTrend: {
                    status: 'done',
                    error: null,
                    current: read(current, scope.current),
                    baseline: read(baseline, scope.baseline),
                },
            });
        } catch (err) {
            console.log('page trend', err);
            if (!this.isMounted_ || pageTrendGeneration !== this.pageTrendGeneration) return;

            this.setState({
                pageTrend: {
                    ...IDLE_PAGE_TREND,
                    status: 'error',
                    error: err?.message ?? this.props.t('global.something_went_wrong'),
                },
            });
        }
    }

    exportName(prefix) {
        const scope = this.state.applied;
        return `${prefix}-${scope?.searchType ?? 'source'}-${scope?.siteUrl ?? 'property'}-${
            scope?.current?.startDate ?? ''
        }`
            .replace(/[^a-z0-9_.-]+/gi, '-')
            .toLowerCase();
    }

    handleExport() {
        const scope = this.state.applied;
        if (!scope || this.state.current.status !== 'done') return;

        downloadCsv(
            buildReportCsv({
                t: this.props.t,
                scope,
                current: this.state.current,
                baseline: this.state.baseline,
                fetchedAt: this.state.fetchedAt
                    ? moment(this.state.fetchedAt).format('YYYY-MM-DD HH:mm')
                    : null,
            }),
            `${this.exportName('discover-news')}.csv`,
        );
    }

    handlePrint() {
        window.print();
    }

    /**
     * Captures the loaded report as an image, from the DOM already on screen — no further request
     * and no upload. The controls and the export actions are left out of the frame, while the
     * source and coverage labels stay in it, so the picture still says which surface it is of.
     */
    async handleDownloadImage() {
        const container = this.reportRef.current;
        if (!container) return;

        this.setState({ capturing: true });

        try {
            const canvas = await html2canvas(container, {
                backgroundColor: '#ffffff',
                scale: 2,
                ignoreElements: (element) =>
                    element.classList?.contains('gsc-tool-controls') ||
                    element.classList?.contains('gsc-tool-controls-bottom') ||
                    element.classList?.contains('gsc-tool-page-actions') ||
                    element.classList?.contains('gsc-tool-icon-actions') ||
                    // The shared chart and table carry their own refresh and download buttons.
                    element.classList?.contains('chart-action-btns') ||
                    element.classList?.contains('dt-actions__bar'),
            });

            const link = document.createElement('a');
            link.href = canvas.toDataURL('image/png');
            link.download = `${this.exportName('discover-news')}.png`;
            link.click();
        } catch (err) {
            console.log(err);
        } finally {
            if (this.isMounted_) this.setState({ capturing: false });
        }
    }

    renderReport(applied) {
        const { t } = this.props;
        const metric = this.metric();
        const comparison = comparability(
            this.state.current.coverage,
            applied.baseline ? this.state.baseline.coverage : null,
        );

        return (
            <>
                <CoverageStrip
                    scope={applied}
                    current={this.state.current}
                    baseline={applied.baseline ? this.state.baseline : null}
                    fetchedAt={this.state.fetchedAt}
                />

                <SourceSummary
                    scope={applied}
                    current={this.state.current}
                    baseline={this.state.baseline}
                    comparison={comparison}
                    metric={metric}
                    onSelectMetric={this.handleSelectMetric}
                />

                <PageChanges
                    scope={applied}
                    status={this.state.pages.status}
                    error={this.state.pages.error}
                    baselineError={this.state.pages.baselineError}
                    comparing={this.state.pages.comparing}
                    rows={this.state.pages.rows}
                    period={this.state.pages.period}
                    shortened={this.state.pages.shortened}
                    stopReason={pagesStopReason(
                        this.state.pages.currentChunk,
                        this.state.pages.priorChunk,
                    )}
                    filter={this.state.pages.filter}
                    loadingMore={this.state.pages.loadingMore}
                    selectedKey={this.state.selectedPage}
                    onSelect={this.handleSelectPage}
                    onLoadMore={this.handleLoadMorePages}
                    onFilter={this.handleFilterPages}
                />

                {/* The block a picked page fills, wrapped so every pick can bring it into view. */}
                <div ref={this.pageTrendRef}>
                    <PageTrend
                        scope={applied}
                        status={this.state.pageTrend.status}
                        error={this.state.pageTrend.error}
                        selectedKey={this.state.selectedPage}
                        current={this.state.pageTrend.current}
                        baseline={this.state.pageTrend.baseline}
                        metric={metric}
                        onMetric={this.handleSelectMetric}
                        onClear={this.handleClearPage}
                    />
                </div>

                <CountryDistribution
                    scope={applied}
                    status={this.state.countries.status}
                    error={this.state.countries.error}
                    rows={this.state.countries.rows}
                    onSelectCountry={this.handleSelectCountry}
                    onClearCountry={this.handleClearCountry}
                />

                <SourceCoverage
                    scope={applied}
                    exportable={this.state.current.status === 'done'}
                    onExport={this.handleExport}
                />

                <p className="gsc-tool-table-note">
                    {t('tools.discover_news.footnote', {
                        source: sourceLabel(t, applied.searchType),
                    })}
                </p>
            </>
        );
    }

    render() {
        const { t } = this.props;
        const scope = this.scope();
        const applied = this.state.applied;
        const ready = this.state.current.status === 'done';

        return (
            <div className="gsc-tool" ref={this.reportRef}>
                <div className="gsc-tool-title-row">
                    <div>
                        <h2 className="page-title">{t('tools.discover_news.header')}</h2>
                        <p className="gsc-tool-sub">{t('tools.discover_news.subtitle')}</p>
                    </div>

                    <div className="gsc-tool-page-actions">
                        <button
                            type="button"
                            className="button button-outline button-primary btn"
                            onClick={this.handleDownloadImage}
                            disabled={!ready || this.state.capturing}
                        >
                            <i className="icon-fi_download" />
                            {this.state.capturing
                                ? t('tools.gsc.exports.capturing')
                                : t('tools.gsc.exports.image')}
                        </button>
                        <button
                            type="button"
                            className="button button-outline button-primary btn"
                            onClick={this.handlePrint}
                            disabled={!ready}
                        >
                            <i className="icon-fi_download" />
                            {t('tools.gsc.exports.full_page')}
                        </button>
                    </div>
                </div>

                {!this.credentialsDomainId && (
                    <div className="gsc-tool-banner error">
                        {t('tools.gsc.controls.no_credentials')}
                    </div>
                )}

                <ScopeControls
                    draft={this.state.draft}
                    scope={scope}
                    applied={applied}
                    open={this.state.controlsOpen}
                    properties={this.state.properties}
                    loadingProperties={this.state.loadingProperties}
                    propertiesError={this.state.propertiesError}
                    latestDate={this.state.latestDate}
                    loading={this.state.loading}
                    onChange={this.handleDraftChange}
                    onApply={this.handleApply}
                    onRefresh={this.handleRefresh}
                    onToggle={this.handleToggleControls}
                    // The shared search-type select is replaced by this report's own source select,
                    // and device is not one of its controls; the rest of the bar is the shared one.
                    fields={['property', 'country', 'period', 'comparison']}
                    // The applied line names the source that was applied, not the one sitting in
                    // the draft: switching the select must not relabel figures still on screen
                    // from the previous source.
                    extraScopeLabels={applied ? [sourceLabel(t, applied.searchType)] : []}
                >
                    <ExtraControls draft={this.state.draft} onChange={this.handleDraftChange} />
                </ScopeControls>

                {!applied ? (
                    <div className="card table-main rounded-main overflow-hidden gsc-tool-block">
                        <div className="card-body gsc-tool-load-state">
                            <span className="gsc-tool-load-icon" aria-hidden="true">
                                <i className="icon-compass-03" />
                            </span>
                            <h4>{t('tools.discover_news.idle_title')}</h4>
                            <p>{t('tools.discover_news.idle')}</p>
                        </div>
                    </div>
                ) : (
                    this.renderReport(applied)
                )}
            </div>
        );
    }
}

export default withTranslation()(DiscoverNews);
